import {useState, useEffect, useContext} from "react";
import ReactGA from "react-ga4";
import axios from "axios";
import { io } from "socket.io-client";
import i18n from "i18next";
import { setSocket } from "./utilities/socketService";
import { SnackbarProvider } from "notistack";
import { SnackbarUtilsConfigurator } from "./utilities/SnackBarUtils";
import { createTheme, ThemeProvider } from "@mui/material";
import "@aws-amplify/ui-react/styles.css";

// import io from "socket.io-client";
import AuthContext from "./contexts/AuthContext";
import Router from "./Router";
import Ad from "./components/Ads/Ad";
import Prompt from "./components/Prompts/Prompt";

import "./App.css";
import "./utilities/i18n";

ReactGA.initialize(`${process.env.REACT_APP_GOOGLE_ANALYTICS}`);

function App() {
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [appStyle, setAppStyle] = useState({});
  const [appConfig, setAppConfig] = useState({});
  const [orgId, setOrgId] = useState(null);
  const [adData, setAdData] = useState(null);
  const [promptData, setPromptData] = useState(null);
  const [socket, setSocket] = useState(null);
  const [showError, setShowError] = useState(false);

  // const socket = useRef(null);

  const theme = createTheme({
    palette: {
      primary: {
        main: appStyle?.primaryColor ? appStyle.primaryColor : "#ef5241",
      },
    },
    typography: {
      fontFamily: appStyle?.fontFamily ? appStyle.fontFamily.join(",") : "",
    },
  });

  useEffect(() => {
    if (!("Notification" in window)) {
      console.log("Browser does not support desktop notification");
    } else {
      // console.log("requesting notifications");
      Notification.requestPermission();
    }
  });

  useEffect(() => {
    const getStyles = async () => {
      try {
        const result = await axios.get(
          `${process.env.REACT_APP_TNFT_SERVER}/style?url=${window.location.host}`
        );
        if (result?.data?.success) {
          setAppStyle(result?.data?.style);
          if (result?.data?.style?.favicon) {
            let fav = document.getElementById("favicon");
            fav.setAttribute("href", result.data.style.favicon);
          }
          if (result?.data?.style?.title) {
            document.title = result.data.style.title;
          }
          if (result?.data?.config) {
            setAppConfig(result.data.config);
          }
          if (result?.data?.orgId) {
            setOrgId(result.data.orgId);
          }
        } else {
          setShowError(true);
        }
      } catch (e) {
        console.error(e.message);
        setShowError(true);
        return;
      }
      setLoading(false);
    };
    getStyles();
  }, []);

  // Failsafe: if styles take too long, stop showing blank screen after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const si = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
    );
    if (si === "true") {
      setSignedIn(true);
    }
  }, []);

  useEffect(() => {
    // Only connect to socket if signedIn is true
    if (signedIn) {
      connectSocket();
    }
  }, [signedIn]);

  useEffect(() => {
    if (appConfig?.defaultLanguage) {
      i18n.changeLanguage(appConfig.defaultLanguage);
    }
  }, [appConfig]);

  const connectSocket = async () => {
    console.log("connecting to socket");
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    const newSocket = io(`${process.env.REACT_APP_TNFT_SERVER}`, {
      path: "/appsock/socket.io",
      auth: { token },
      transports: ["websocket"], // Prefer direct WebSocket to avoid sticky-session issues during polling
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 5000,
    });

    newSocket.on("connect_error", (err) => {
      console.error("connect_error", {
        message: err?.message,
        data: err?.data,
        description: err?.description,
        type: err?.type,
      });
    });

    newSocket.on("error", (err) => {
      console.error("socket_error", err);
    });

    newSocket.io.on("reconnect_error", (err) => {
      console.error("reconnect_error", err?.message || err);
    });

    newSocket.io.on("reconnect_failed", () => {
      console.error("reconnect_failed after attempts");
    });

    // Log which transport is actually used
    newSocket.on("connect", () => {
      console.log(
        "Socket connected using transport:",
        newSocket.io.engine.transport.name
      );
    });

    // If the transport upgrades later, log it
    newSocket.io.engine.on("upgrade", (transport) => {
      console.log("Transport upgraded to:", transport.name);
    });

    newSocket.on("connect", () => {
      console.log("Connected to the server");
      setSocket(newSocket);
    });

    newSocket.on("disconnect", () => {
      console.log("Disconnected from the server");
    });

    // Listen for the "couplePriceUpdated" event
    newSocket.on("showad", (data) => {
      console.log("showing ad");
      setAdData(data);
    });

    // Clean up on component unmount
    return () => {
      newSocket.disconnect();
    };
  };

  if (loading) {
    return <div />;
  }

  return (
    <AuthContext.Provider
      value={{
        signedIn,
        setSignedIn,
        email,
        setEmail,
        token,
        setToken,
        appStyle,
        appConfig,
        orgId,
        socket,
        promptData,
        setPromptData,
      }}
    >
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <SnackbarUtilsConfigurator />
          <Router showError={showError} setShowError={setShowError}/>
          <Ad data={adData} close={() => setAdData(null)} />
          <Prompt
            data={promptData}
            close={() => {
              console.log("closing prompt");
              setPromptData(null);
            }}
          />

        </SnackbarProvider>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;
